import { onBeforeUnmount, onMounted, Ref } from "@vue/composition-api";

// iPhone Safari does not shrink the page when the keyboard opens: only the visual viewport gets smaller and
// the page is scrolled so the focused input stays visible. A full-height fixed layout (chat) then shows only
// its bottom part: the header and the messages above the input end up behind the top edge of the screen.
// The hook keeps the element exactly over the visible area while the keyboard is open.
export default function useVisualViewportFit(
  elementRef: Ref<HTMLElement | undefined>,
  onKeyboardChange?: (opened: boolean) => void
) {
  let keyboardOpened = false;
  let frame = 0;

  const reset = (el: HTMLElement) => {
    el.style.height = "";
    el.style.bottom = "";
    el.style.transform = "";
  };

  const update = () => {
    frame = 0;
    const viewport = window.visualViewport;
    const el = elementRef.value;
    if (!viewport || !el) return;
    // pinch zoom also shrinks the visual viewport: the layout stays as it is then
    const zoomed = Math.abs(viewport.scale - 1) > 0.01;
    // iOS 26+ shrinks window.innerHeight too, but the page stays taller than the visible area and is scrolled (offsetTop)
    const fullHeight = document.documentElement.clientHeight;
    const opened = !zoomed && (fullHeight - viewport.height > 1 || viewport.offsetTop > 0);
    if (opened) {
      el.style.height = `${viewport.height}px`;
      el.style.bottom = "auto";
      el.style.transform = `translateY(${viewport.offsetTop}px)`;
    } else {
      reset(el);
    }
    if (opened !== keyboardOpened) {
      keyboardOpened = opened;
      onKeyboardChange && onKeyboardChange(opened);
    }
  };

  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };

  onMounted(() => {
    if (!window.visualViewport) return;
    window.visualViewport.addEventListener("resize", schedule);
    window.visualViewport.addEventListener("scroll", schedule);
    update();
  });

  onBeforeUnmount(() => {
    if (frame) window.cancelAnimationFrame(frame);
    if (!window.visualViewport) return;
    window.visualViewport.removeEventListener("resize", schedule);
    window.visualViewport.removeEventListener("scroll", schedule);
  });

  return { update };
}
